// Centralized validation rules shared across Landing Page + all 3 portals.
// Mirrors the backend rules in /app/backend/validators.py so the UI blocks
// bad input before it ever hits the API.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_RE = /^[6-9]\d{9}$/;

export function normalizeEmail(email) {
  return (email || "").trim().toLowerCase();
}

export function validateEmail(email) {
  const cleaned = normalizeEmail(email);
  if (!cleaned) return "Email is required";
  if (cleaned.length > 254) return "Email is too long";
  if (!EMAIL_RE.test(cleaned)) return "Enter a valid email address";
  return null;
}

export function cleanMobile(mobile) {
  return (mobile || "").replace(/[\s\-()]/g, "");
}

export function validateMobile(mobile) {
  const cleaned = cleanMobile(mobile);
  if (!cleaned) return "Mobile number is required";
  if (!MOBILE_RE.test(cleaned)) return "Enter a valid 10-digit Indian mobile number (starts with 6-9)";
  return null;
}

export function getPasswordChecks(password) {
  const pwd = password || "";
  return {
    length: pwd.length >= 8 && pwd.length <= 32,
    upper: /[A-Z]/.test(pwd),
    lower: /[a-z]/.test(pwd),
    number: /\d/.test(pwd),
    special: /[^A-Za-z0-9]/.test(pwd),
  };
}

export function validatePassword(password) {
  if (!password) return "Password is required";
  const c = getPasswordChecks(password);
  if (!c.length) return "Password must be 8-32 characters";
  if (!c.upper) return "Add an uppercase letter";
  if (!c.lower) return "Add a lowercase letter";
  if (!c.number) return "Add a number";
  if (!c.special) return "Add a special character";
  return null;
}

export function getPasswordStrength(password) {
  const c = getPasswordChecks(password);
  const score = Object.values(c).filter(Boolean).length;
  if (!password) return { score: 0, label: "", color: "bg-slate-200" };
  if (score <= 2) return { score, label: "Weak", color: "bg-red-500" };
  if (score <= 4) return { score, label: "Fair", color: "bg-amber-500" };
  return { score, label: "Strong", color: "bg-green-600" };
}

export function validateConfirmPassword(password, confirm) {
  if (!confirm) return "Please confirm the password";
  if (password !== confirm) return "Passwords do not match";
  return null;
}

export function calculateAge(dob) {
  if (!dob) return "";
  const birth = new Date(`${dob}T00:00:00`);
  if (Number.isNaN(birth.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
  return age >= 0 ? age : "";
}

export function validateDOB(dob) {
  if (!dob) return null;
  const date = new Date(`${dob}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "Enter a valid date";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (date > today) return "Date of birth cannot be in the future";
  const age = calculateAge(dob);
  if (age > 130) return "Please enter a valid date of birth";
  return null;
}

export function validateAge(age) {
  if (age === "" || age === null || age === undefined) return null;
  const n = Number(age);
  if (Number.isNaN(n) || n < 0 || n > 130) return "Age must be between 0 and 130";
  return null;
}

export function validateNotFutureDate(dateStr, label = "Date") {
  if (!dateStr) return null;
  const date = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "Enter a valid date";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (date > today) return `${label} cannot be in the future`;
  return null;
}
